import type { DmListPosition } from '../../../services/dmNotifications';
import { plural } from '../../../utils/plural.ts';

export interface KnownListPosition {
    position: number;
    total: number;
}

export type ListPositionKind = 'inList' | 'leftList' | 'notInList';

export interface ListPositionLabel {
    kind: ListPositionKind;
    // Shown as "<current> of <total>", current emphasised; current is a dash when there is none.
    current: string;
    total: string;
    ariaLabel: string;
    // Why the patient isn't counted in the list right now; null while it is.
    note: string | null;
}

export const LEFT_LIST_NOTE =
    'No longer in this list. Previous and Next continue from where you were.';
export const NOT_IN_LIST_NOTE = 'Not in this list.';
const UNKNOWN_POSITION = '–';

function parts(
    current: number | null,
    total: number
): Pick<ListPositionLabel, 'current' | 'total'> {
    return {
        current: current === null ? UNKNOWN_POSITION : current.toLocaleString(),
        total: total.toLocaleString()
    };
}

export function listPositionText({ current, total }: ListPositionLabel): string {
    return `${current} of ${total}`;
}

function ofTotal({ position, total }: KnownListPosition): string {
    return `${position.toLocaleString()} of ${total.toLocaleString()}`;
}

export function knownListPosition(latest: DmListPosition | undefined): KnownListPosition | null {
    if (!latest || latest.position === null) return null;
    return { position: latest.position, total: latest.total };
}

// The last place the patient held, so dropping out of the filters doesn't lose it.
export function rememberListPosition(
    previous: KnownListPosition | null,
    latest: DmListPosition | undefined
): KnownListPosition | null {
    return knownListPosition(latest) ?? previous;
}

// Null while the position is unknown: still loading, or a server that doesn't send it.
export function listPositionLabel(
    latest: DmListPosition | undefined,
    lastKnown: KnownListPosition | null
): ListPositionLabel | null {
    if (!latest) return null;
    const current = knownListPosition(latest);
    if (current) {
        return {
            kind: 'inList',
            ...parts(current.position, current.total),
            ariaLabel: `Patient ${ofTotal(current)} in this list`,
            note: null
        };
    }
    if (lastKnown) {
        return {
            kind: 'leftList',
            ...parts(lastKnown.position, lastKnown.total),
            ariaLabel: `No longer in this list, was patient ${ofTotal(lastKnown)}`,
            note: LEFT_LIST_NOTE
        };
    }
    return {
        kind: 'notInList',
        ...parts(null, latest.total),
        ariaLabel: `Not in this list of ${plural(latest.total, 'patient')}`,
        note: NOT_IN_LIST_NOTE
    };
}
